import sodium from "libsodium-wrappers";
import compresor from "./compress";

/* ================================
   Types
================================ */
export type EncryptInput = string | object | any[];

/* ================================
   Sodium Init (SAFE & IDP)
================================ */
let initPromise: Promise<void> | null = null;

async function init(): Promise<void> {
   if (!initPromise) {
      initPromise = sodium.ready.then(() => void 0);
   }
   return initPromise;
}

/* ================================
   Key Derivation
   (Stable & deterministic)
================================ */
function deriveKey(secret: string): Uint8Array {
   return sodium.crypto_generichash(
      sodium.crypto_secretbox_KEYBYTES,
      sodium.from_string(secret),
      null
   ) as Uint8Array
}

/* ================================
   Encrypt (string)
================================ */
async function encrypt(
   input: EncryptInput,
   secret: string
): Promise<string> {
   const buf = await encryptBuffer(input, secret);
   return sodium.to_base64(
      buf,
      sodium.base64_variants.URLSAFE_NO_PADDING
   );
}

/* ================================
   Decrypt (string)
================================ */
async function decrypt(
   base64: string,
   secret: string
): Promise<any | null> {
   await init();

   const box = sodium.from_base64(
      base64,
      sodium.base64_variants.URLSAFE_NO_PADDING
   );

   return decryptBuffer(box, secret);
}

/* ================================
   Encrypt (buffer)
================================ */
async function encryptBuffer(
   input: EncryptInput,
   secret: string
): Promise<Uint8Array> {
   await init();

   const key = deriveKey(secret);
   const nonce = sodium.randombytes_buf(
      sodium.crypto_secretbox_NONCEBYTES
   ) as Uint8Array


   const compressed = await compresor.compressBuffer(input);

   const cipher = sodium.crypto_secretbox_easy(
      compressed,
      nonce,
      key
   ) as Uint8Array

   const out = new Uint8Array(nonce.length + cipher.length);
   out.set(nonce);
   out.set(cipher, nonce.length);

   return out;
}

/* ================================
   Decrypt (buffer)
================================ */
async function decryptBuffer(
   box: Uint8Array,
   secret: string
): Promise<any | null> {
   await init();

   const key = deriveKey(secret);
   const nonceSize = sodium.crypto_secretbox_NONCEBYTES;

   if (box.length <= nonceSize) return null;

   const nonce = box.subarray(0, nonceSize);
   const cipher = box.subarray(nonceSize);

   const opened = sodium.crypto_secretbox_open_easy(
      cipher,
      nonce,
      key
   ) as Uint8Array

   if (!opened) return null;

   return compresor.decompressBuffer(opened);
}

/* ================================
   Hash (URL-safe, short)
================================ */
async function hash(str: string): Promise<string> {
   await init();

   const digest = sodium.crypto_generichash(
      32,
      sodium.from_string(str),
      null
   );

   return sodium
      .to_base64(digest, sodium.base64_variants.URLSAFE_NO_PADDING)
      .replace(/[^a-zA-Z0-9]/g, "");
}

/* ================================
   Canonical JSON
================================ */
function canonicalJSON(value: any): string {
   if (value === null || typeof value !== "object") {
      return JSON.stringify(value);
   }

   if (Array.isArray(value)) {
      return JSON.stringify(value.map(canonicalJSON));
   }

   const keys = Object.keys(value).sort();
   const obj: any = {};

   for (const k of keys) {
      obj[k] = value[k];
   }

   return JSON.stringify(obj);
}

/* ================================
   Create Token
================================ */
async function createToken(
   payload: Record<string, any>,
   secret: string
): Promise<string> {
   await init();

   const key = deriveKey(secret);
   const json = canonicalJSON(payload);

   const encryptedPayload = await encrypt(payload, secret);

   const mac = sodium.crypto_generichash(
      32,
      sodium.from_string(json),
      key
   );

   const macBase64 = sodium.to_base64(
      mac,
      sodium.base64_variants.URLSAFE_NO_PADDING
   );

   return `${encryptedPayload}.${macBase64}`;
}

/* ================================
   Verify Token
================================ */
async function verifyToken(
   token: string,
   secret: string
): Promise<{ valid: boolean; payload?: any }> {
   await init();

   const parts = token.split(".");
   if (parts.length !== 2) return { valid: false };

   const [payloadBase64, macBase64] = parts;

   const payload = await decrypt(payloadBase64, secret);
   if (payload === null) return { valid: false };

   const json = canonicalJSON(payload);
   const key = deriveKey(secret);

   const expectedMac = sodium.crypto_generichash(
      32,
      sodium.from_string(json),
      key
   ) as Uint8Array

   const mac = sodium.from_base64(
      macBase64,
      sodium.base64_variants.URLSAFE_NO_PADDING
   );

   if (
      mac.length !== expectedMac.length ||
      !sodium.memcmp(expectedMac, mac)
   ) {
      return { valid: false };
   }

   return { valid: true, payload };
}



const crypto = {
   init,
   encrypt,
   decrypt,
   encryptBuffer,
   decryptBuffer,
   hash,
   createToken,
   verifyToken,
}

export default crypto